import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
  TablePagination,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface TransactionLog {
  id: number;
  companyId: string;
  transactionId: number;
  transactionFirebaseId?: string;
  changedField: string;
  oldValue?: string;
  newValue?: string;
  reason?: string;
  createdBy: string;
  createdAt: string;
}

interface TransactionLogsModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
}

const TransactionLogsModal: React.FC<TransactionLogsModalProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const { t, i18n } = useTranslation('accounting');
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [logs, setLogs] = useState<TransactionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Logları yükle
  useEffect(() => {
    if (!open || !companyId) return;

    const loadLogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await accountingApi.get(ACCOUNTING_API.TRANSACTION_LOGS_BY_COMPANY);

        const result = response.data;
        const logsData = result.data || result;

        // Tarihe göre sırala (en yeni en üstte)
        logsData.sort((a: TransactionLog, b: TransactionLog) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setLogs(logsData);
      } catch (error) {
        console.error('❌ Loglar yüklenirken hata:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [open, companyId]);

  // Tarih formatla
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(localeCode, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  // Değişiklik türüne göre renk
  const getChangeTypeColor = (changedField: string) => {
    switch (changedField) {
      case 'DELETE_TRANSACTION':
        return 'error';
      case 'UPDATE_PAYMENT_METHOD':
        return 'warning';
      case 'CREATE_TRANSACTION':
        return 'success';
      default:
        return 'default';
    }
  };

  // Değişiklik türüne göre isim
  const getChangeTypeName = (changedField: string) => {
    const keyMap: Record<string, string> = {
      DELETE_TRANSACTION: 'current.report.logsModal.changeTypes.deleteTransaction',
      UPDATE_PAYMENT_METHOD: 'current.report.logsModal.changeTypes.updatePaymentMethod',
      CREATE_TRANSACTION: 'current.report.logsModal.changeTypes.createTransaction',
    };

    const key = keyMap[changedField];
    return key ? t(key) : changedField;
  };

  const translateTransactionTypeCode = (type?: string) => {
    if (!type) return '-';

    const normalized = type.toUpperCase();
    const keyMap: Record<string, string> = {
      PAYMENT: 'current.report.logsModal.transactionTypes.payment',
      COLLECTION: 'current.report.logsModal.transactionTypes.collection',
      INVOICE: 'current.report.logsModal.transactionTypes.invoice',
      SALE_WITH_STOCK: 'current.report.logsModal.transactionTypes.saleWithStock',
      SALE_WITHOUT_STOCK: 'current.report.logsModal.transactionTypes.saleWithoutStock',
      QUICK_SALE: 'current.report.logsModal.transactionTypes.quickSale',
      CHECK_RETURNED: 'current.report.logsModal.transactionTypes.checkReturned',
      NOTE_RETURNED: 'current.report.logsModal.transactionTypes.noteReturned',
      CHECK_GIVEN: 'current.report.logsModal.transactionTypes.checkGiven',
      NOTE_GIVEN: 'current.report.logsModal.transactionTypes.noteGiven',
    };

    const key = keyMap[normalized];
    return key ? t(key) : type;
  };

  const translateLogValue = (value?: string) => {
    if (!value) return '-';

    const normalized = value.trim().toUpperCase();
    const keyMap: Record<string, string> = {
      DELETED: 'current.report.logsModal.values.deleted',
      UPDATED: 'current.report.logsModal.values.updated',
      CREATED: 'current.report.logsModal.values.created',
    };

    const key = keyMap[normalized];
    return key ? t(key) : value;
  };

  // OldValue'yu parse et ve formatla
  const parseOldValue = (oldValue: string | undefined, changedField: string) => {
    if (!oldValue) return '-';
    
    // DELETE_TRANSACTION için özel format
    if (changedField === 'DELETE_TRANSACTION') {
      // Format: ID:1306|Type:PAYMENT|Amount:1500000|Date:2025-12-02
      const parts = oldValue.split('|');
      const data: any = {};
      
      parts.forEach(part => {
        const [key, value] = part.split(':');
        if (key && value) {
          data[key] = value;
        }
      });
      
      const typeName = translateTransactionTypeCode(data.Type);
      const amount = data.Amount ? parseFloat(data.Amount).toLocaleString(localeCode, {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) : '-';
      
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
            {typeName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
            {amount}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {data.Date || '-'}
          </Typography>
        </Box>
      );
    }
    
    // Diğer işlem türleri için direkt göster
    return translateLogValue(oldValue);
  };

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sayfalandırılmış loglar
  const paginatedLogs = logs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          <Typography variant="h6">{tt('İşlem Logları', 'Transaction Logs')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setShowInfoBox(prev => !prev)}
            sx={{ color: 'white' }}
            size="small"
          >
            <InfoOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && showInfoBox && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              {tt('İşlem Logları Hakkında Kısa Bilgi', 'Quick Info About Transaction Logs')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {tt('Bu kayıtlar cari hareketler üzerinde yapılan kritik değişiklikleri geçmişe dönük izleme için gösterir.', 'These records show critical changes made on current account movements for historical traceability.')}
            </Typography>
            <Typography variant="body2">
              {tt('• İşlem türü, eski/yeni değer ve değişiklik nedeni denetim amaçlı saklanır.', '• Transaction type, old/new values, and reason are stored for audit purposes.')}
            </Typography>
            <Typography variant="body2">
              {tt('• Kayıtlar en yeni tarihten eskiye doğru listelenir.', '• Records are listed from newest to oldest.')}
            </Typography>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px' 
          }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '300px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
            borderRadius: 2
          }}>
            <Typography variant="h6" component="h2" gutterBottom color="primary.main" fontWeight="bold">
              {tt('Henüz log kaydı bulunmamaktadır.', 'No log records found yet.')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '0.875rem', 
                        bgcolor: 'primary.main' 
                      }}
                    >
                      {tt('Tarih', 'Date')}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '0.875rem', 
                        bgcolor: 'primary.main' 
                      }}
                    >
                      {tt('Transaction ID', 'Transaction ID')}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '0.875rem', 
                        bgcolor: 'primary.main' 
                      }}
                    >
                      {tt('İşlem Türü', 'Transaction Type')}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '0.875rem', 
                        bgcolor: 'primary.main' 
                      }}
                    >
                      {tt('Eski Değer', 'Old Value')}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '0.875rem', 
                        bgcolor: 'primary.main' 
                      }}
                    >
                      {tt('Yeni Değer', 'New Value')}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '0.875rem', 
                        bgcolor: 'primary.main' 
                      }}
                    >
                      {tt('Sebep', 'Reason')}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '0.875rem', 
                        bgcolor: 'primary.main' 
                      }}
                    >
                      {tt('İşlemi Yapan', 'Created By')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLogs.map((log) => (
                    <TableRow 
                      key={log.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {formatDate(log.createdAt)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', fontWeight: 'medium' }}>
                        #{log.transactionId}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        <Chip 
                          label={getChangeTypeName(log.changedField)} 
                          color={getChangeTypeColor(log.changedField)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', minWidth: '180px' }}>
                        {parseOldValue(log.oldValue, log.changedField)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {translateLogValue(log.newValue)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.reason || '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {log.createdBy}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={logs.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              labelRowsPerPage={tt('Sayfa başına satır:', 'Rows per page:')}
              labelDisplayedRows={({ from, to, count }) => isTr ? `${count} kayıttan ${from}-${to} arası gösteriliyor` : `${from}-${to} of ${count}`}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionLogsModal;

